import { blockNextAtom } from '@/stores/tournament-step';
import { ArrowRight, ClipboardList, Loader2, Trophy } from 'lucide-react';
import CloseInscriptions from './close-inscriptions';
import { clubApi } from '@repo/api-client';
import { useAtomValue } from 'jotai';
import { useRevalidator } from 'react-router';
import { Button } from '@repo/ui/components/button';
import { useAsync } from '@repo/ui/hooks/use-async';
import { GeneralError } from '@repo/ui/components/form/general-error';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@repo/ui/components/dialog';

const TournamentFooter = ({ tournament }: { tournament: Tournament }) => {
  const revalidator = useRevalidator();
  const blockNext = useAtomValue(blockNextAtom);

  const { execute, loading, error } = useAsync(
    async (data: { status: Tournament['status']; mode?: Tournament['mode'] }) => {
      await clubApi.updateTournamentById(tournament.club.id, tournament.id, data);
      revalidator.revalidate();
    }
  );

  return (
    <div className="py-3 border-t">
      {(error || loading) && (
        <div className="flex justify-center mb-2 gap-2">
          {error && <GeneralError errors={error} />}
          {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        </div>
      )}
      {tournament?.status === 'PENDING' && (
        <div className="flex justify-center">
          <Dialog>
            <DialogTrigger>
              <Button disabled={blockNext} size="xl">
                Siguiente <ArrowRight />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg!">
              <DialogTitle>Tipo de torneo</DialogTitle>
              <p className="text-sm text-muted-foreground">
                Selecciona como quieres gestionar este torneo
              </p>
              <div className="grid gap-4 mt-2">
                <button
                  className="flex items-start gap-4 rounded-xl border-2 border-neutral-200 bg-white p-5 text-left transition-colors hover:border-primary hover:bg-primary/5 cursor-pointer"
                  onClick={() =>
                    // TODO: Maybe mode is not necesary any more
                    execute({ mode: 'INSCRIPTION_ONLY', status: 'CONFIGURING_DATETIME_PHASES' })
                  }
                >
                  <div className="rounded-lg bg-blue-100 p-3 shrink-0">
                    <ClipboardList className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-base font-semibold">Solo inscripcion</span>
                    <span className="text-sm text-muted-foreground">
                      Gestiona las inscripciones del torneo configurando horarios disponibles, sin
                      fase de eliminación ni cuadro de juego.
                    </span>
                  </div>
                </button>
                <div
                  className="flex items-start gap-4 rounded-xl border-2 border-neutral-200 bg-neutral-50 p-5 text-left opacity-75 cursor-not-allowed"
                  aria-disabled
                >
                  <div className="rounded-lg bg-green-100 p-3 shrink-0">
                    <Trophy className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-base font-semibold">Torneo completo</span>
                    <span className="text-sm text-muted-foreground">
                      Configura horarios, fase de grupos y eliminacion para un torneo completo.
                    </span>
                    <span className="text-sm font-medium text-amber-600 mt-1">
                      Disponible pronto
                    </span>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
      {tournament?.status === 'CONFIGURING_DATETIME_PHASES' && (
        <div className="flex justify-center">
          <Button
            onClick={() => execute({ status: 'OPEN_INSCRIPTIONS' })}
            disabled={blockNext}
            size="xl"
          >
            Abrir inscripciones <ArrowRight />
          </Button>
        </div>
      )}
      {tournament?.status === 'OPEN_INSCRIPTIONS' && (
        <div className="flex justify-center">
          {tournament.mode === 'INSCRIPTION_ONLY' ? (
            <Button onClick={() => execute({ status: 'FINISHED' })} disabled={blockNext} size="xl">
              Cerrar inscripciones
            </Button>
          ) : (
            <CloseInscriptions tournament={tournament} />
          )}
        </div>
      )}
      {tournament?.status === 'ACTIVE' && (
        <div className="flex justify-center">
          <Button onClick={() => execute({ status: 'FINISHED' })} disabled={blockNext} size="xl">
            Finalizar torneo
          </Button>
        </div>
      )}
    </div>
  );
};

export default TournamentFooter;
